import { isValid, parseISO } from 'date-fns';

import { DeliverableType } from 'src/shared/constants/DeliverableType';
import type { ProfileQuery } from 'src/shared/graphql/generated';
import {
  JobSearchFilters,
  JobTypeFilters,
  JobsViewableBy,
} from 'src/shared/graphql/generated';

import type { FiltersParams } from '../components/Filters/types';

type FilterInputOption = {
  name: string;
  value: string;
};

export const ALL_FILTER_INPUT_OPTION: FilterInputOption = {
  name: 'All',
  value: 'all',
};

export const MEASUREMENT_STATUS_FILTERS_INPUTS: FilterInputOption[] = [
  { name: 'Done', value: JobSearchFilters.Complete },
  { name: 'Processing', value: JobSearchFilters.Processing },
  { name: 'Failed', value: JobSearchFilters.Failed },
  { name: 'Photos Needed', value: JobSearchFilters.PhotosNeeded },
  { name: 'Approval Needed', value: JobSearchFilters.NeedsApproval },
];

const JOB_TYPE_FILTERS_INPUTS: FilterInputOption[] = [
  { name: 'Standard', value: JobTypeFilters.Standard },
  { name: 'Connect', value: JobTypeFilters.Connect },
  { name: 'Prospect', value: JobTypeFilters.Prospect },
  { name: 'New Construction', value: JobTypeFilters.Blueprint },
];

const omitInput = (inputList: FilterInputOption[], valueString: string) => {
  const filteredArray = inputList.filter(input => input.value !== valueString);
  return filteredArray;
};

export const allowedMeasurementStatusFilters = (
  isProspectEnabled: boolean,
  isLeadHomeownerCaptureEnabled: boolean,
) => {
  let measureStatFilters = [...MEASUREMENT_STATUS_FILTERS_INPUTS];

  // if user does not have photo capture / prospect, remove needs_photos from measurement status filters
  if (!isProspectEnabled) {
    measureStatFilters = omitInput(
      measureStatFilters,
      JobSearchFilters.PhotosNeeded,
    );
  }

  // if user does not have lead homeowner capture, remove needs_approval from measurement status filters
  if (!isLeadHomeownerCaptureEnabled) {
    measureStatFilters = omitInput(
      measureStatFilters,
      JobSearchFilters.NeedsApproval,
    );
  }

  return measureStatFilters;
};

export const allowedJobTypeFilters = (
  isProspectEnabled: boolean,
  isLeadHomeownerCaptureEnabled: boolean,
  isBlueprintsEnabled: boolean,
) => {
  // start with the set of all job type filters
  let jobTypeFilters = [...JOB_TYPE_FILTERS_INPUTS];

  // if user does not have photo capture / prospect, remove connect from job type filters
  if (!isProspectEnabled) {
    jobTypeFilters = omitInput(jobTypeFilters, JobTypeFilters.Connect);
  }

  // if user does not have lead homeowner capture, remove prospect from job type filters
  if (!isLeadHomeownerCaptureEnabled) {
    jobTypeFilters = omitInput(jobTypeFilters, JobTypeFilters.Prospect);
  }

  // if user does not have blueprints, remove new construction from job type filters
  if (!isBlueprintsEnabled) {
    jobTypeFilters = omitInput(jobTypeFilters, JobTypeFilters.Blueprint);
  }

  // return the jobTypeFilters plus the "all" option input
  return [ALL_FILTER_INPUT_OPTION, ...jobTypeFilters];
};

export type OrgsAvailableDeliverablesType =
  | ProfileQuery['profile']['user']['orgs'][0]['availableDeliverables']
  | undefined;

export const allowedMeasurementTypeFilters = (
  orgsAvailableDeliverables: OrgsAvailableDeliverablesType,
) => {
  const measurementTypeFilterInputs =
    orgsAvailableDeliverables?.map(deliverable => ({
      name: deliverable.name || '',
      value: deliverable.id.toString(),
    })) || [];

  return [...measurementTypeFilterInputs] as FilterInputOption[];
};

export const getDeliverableNameById = (id: number) => {
  const indexOf = Object.values(DeliverableType).indexOf(
    id as unknown as DeliverableType,
  );

  return Object.keys(DeliverableType)[indexOf];
};

const toTitleCase = (str: string) =>
  str[0].toUpperCase() + str.slice(1).toLowerCase();

export const jobTypeDisplayName = (jobType: JobTypeFilters) => {
  if (jobType === JobTypeFilters.Blueprint) {
    return 'New Construction';
  }
  return toTitleCase(jobType);
};

export const measurementTypeDisplayName = (measurementType: string) => {
  if (measurementType.toLowerCase() === 'roof') {
    return 'Roof only';
  }
  return measurementType;
};

const jobSearchFilters = Object.values(JobSearchFilters);
const jobTypeFilters = Object.values(JobTypeFilters);
const jobsViewableBy = Object.values(JobsViewableBy);

export const defaultJobFilters = {
  search: undefined,
  filters: [],
  jobTypes: [],
  deliverableIds: [],
  example: false,
  archived: false,
  viewableBy: JobsViewableBy.All,
  createdAfter: undefined,
  createdBefore: undefined,
  creatorIds: [],
};

// Function to ensure we only allow adding valid filter parameters.
// This function should only return a `FiltersParams` object if there are valid filters.
// Otherwise, it should return `null` to represent no filters are being applied and
// the defaults should be used.
export const createJobFiltersFromUrlSearchParams = (
  searchParams: URLSearchParams,
): FiltersParams | null => {
  const filters: FiltersParams = {};
  const searchTerm = searchParams.get('search');
  const filterParams = searchParams.getAll('filters');
  const jobTypeFilter = searchParams.get('jobTypes');
  const deliverableIdFilter = searchParams.getAll('deliverableIds');
  const creatorIdsParam = searchParams.getAll('creatorIds');
  const examplesParam = searchParams.get('example');
  const archivedParam = searchParams.get('archived');
  const viewableByParam = searchParams.get('viewableBy');
  const createdAfterParam = searchParams.get('createdAfter');
  const createdBeforeParam = searchParams.get('createdBefore');

  if (searchTerm) {
    filters.search = searchTerm;
  }

  if (createdAfterParam) {
    const date = parseISO(createdAfterParam);
    if (isValid(date)) {
      filters.createdAfter = date;
    } else {
      reportError('Invalid date set for createdAfter filter.');
    }
  }

  if (createdBeforeParam) {
    const date = parseISO(createdBeforeParam);
    if (isValid(date)) {
      filters.createdBefore = date;
    } else {
      reportError('Invalid date set for createdBefore filter.');
    }
  }

  if (creatorIdsParam.length) {
    filters.creatorIds = creatorIdsParam;
  }

  if (filterParams.length) {
    const searchFilters = filterParams.filter(filter =>
      jobSearchFilters.includes(filter as JobSearchFilters),
    );

    if (searchFilters.length) {
      filters.filters = searchFilters as JobSearchFilters[];
    }
  }

  if (deliverableIdFilter.length) {
    // Make sure the filter is a valid filter
    const deliverableIds = deliverableIdFilter.filter(id =>
      Object.hasOwn(DeliverableType, id),
    );

    if (deliverableIds.length) {
      filters.deliverableIds = deliverableIds;
    }
  }

  if (
    jobTypeFilter &&
    jobTypeFilters.includes(jobTypeFilter as JobTypeFilters)
  ) {
    filters.jobTypes = [jobTypeFilter as JobTypeFilters];
  }

  if (
    viewableByParam &&
    jobsViewableBy.includes(viewableByParam as JobsViewableBy)
  ) {
    filters.viewableBy = viewableByParam as JobsViewableBy;
  }

  if (examplesParam === 'true') {
    filters.example = true;
  }

  if (archivedParam === 'true') {
    filters.archived = true;
  }

  return Object.keys(filters).length ? filters : null;
};
